import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api, fileUrl } from "../api";
import { Reveal } from "../components/Section";
import { NewsletterForm } from "../components/NewsletterForm";

export default function JournalArticle() {
    const { slug } = useParams();
    const [article, setArticle] = useState(null);
    const [missing, setMissing] = useState(false);

    useEffect(() => {
        api.get(`/journal/${slug}`).then((r) => setArticle(r.data)).catch(() => setMissing(true));
    }, [slug]);

    if (missing) {
        return (
            <div className="pt-40 pb-24 text-center" data-testid="article-not-found">
                <p className="font-display font-extrabold text-2xl text-lakou-cream">Article not found</p>
                <Link to="/journal" className="mt-4 inline-block font-mono2 text-xs uppercase tracking-[0.2em] text-lakou-gold">Back to the Journal</Link>
            </div>
        );
    }
    if (!article) return <div className="pt-40 pb-24 text-center text-lakou-sand text-sm">Loading…</div>;

    const readMins = Math.max(1, Math.round(article.body.split(/\s+/).length / 200));

    return (
        <div data-testid="article-page" className="pt-32 pb-24">
            <article className="max-w-3xl mx-auto px-4 sm:px-6">
                <Link to="/journal" data-testid="article-back" className="inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-lakou-gold hover:text-lakou-goldbright transition-colors">
                    <ArrowLeft size={13} /> Journal
                </Link>
                <Reveal>
                    <div className="mt-8 font-mono2 text-[10px] uppercase tracking-[0.3em] text-lakou-gold">{article.category}</div>
                    <h1 className="mt-4 font-display font-extrabold tracking-tight text-3xl sm:text-5xl text-lakou-cream leading-[1.08]" data-testid="article-title">{article.title}</h1>
                    <div className="mt-5 font-mono2 text-xs text-lakou-sand">{article.author}{article.published_at ? ` · ${new Date(article.published_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}` : ""} · {readMins} min read</div>
                </Reveal>
                {article.cover_path && (
                    <Reveal delay={0.1}>
                        <img src={fileUrl(article.cover_path)} alt={article.title} className="mt-10 w-full rounded-3xl border border-lakou-gold/20 object-cover aspect-video" />
                    </Reveal>
                )}
                <Reveal delay={0.15}>
                    <div className="mt-10 space-y-6 text-lakou-offwhite/85 leading-relaxed text-base sm:text-lg" data-testid="article-body">
                        {article.body.split("\n").filter(Boolean).map((p, i) => (
                            <p key={i} className={i === 0 ? "first-letter:font-display first-letter:text-6xl first-letter:font-black first-letter:text-lakou-gold first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85]" : ""}>{p}</p>
                        ))}
                    </div>
                </Reveal>
                <Reveal delay={0.2}>
                    <div className="mt-16 rounded-3xl border border-lakou-gold/20 bg-lakou-coal/70 p-8">
                        <div className="font-display font-extrabold text-xl text-lakou-cream">Never miss a story</div>
                        <p className="mt-2 text-sm text-lakou-grey">New articles, cities and drops — straight to your inbox.</p>
                        <div className="mt-6"><NewsletterForm compact /></div>
                    </div>
                </Reveal>
            </article>
        </div>
    );
}
